/**
 * Created by mzhao on 09/28/2021.
 * Description:
 * ExchangeImportListDialog Component
 * ------ maintenance history ------
 * Updated by Daniel Wang on 05/9/2022, Added logic to hanlde the duplicate contats between exchange and tamale
 * Updated by Daniel Wang on 09/01/2022, User should able to import and one-way connect the contact.
 */
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { catchError, filter, take } from 'rxjs/operators';
import { of as observableOf } from 'rxjs';
import { ExchangeListDialogOptions } from './exchange-import-list-dialog.model';
import { ExchangeImportListDialogService } from './exchange-import-list-dialog.service';
import { ExchangeContactDetail, ExchangeImportDataModel, ExchangeImportDetailDataModel, ExchangeImportInputDataModel, ExchangeModel, ExchangeReturnDataModel, MergedContactBase, NeedReturnContact } from '../../../tamalelibs/models/exchange-import.model';
import { ExchangeService } from '../../../tamalelibs/services/exchange.service';
import { ToastService } from '../../../widgets/toast/toast.service';
import { NotificationStyles } from '../../../widgets/notification/notification.model';
import { DialogAction, DialogService, DialogType } from '../../../services/dialog.service';
import { StringLiteralsPipe } from '../../../pipes/translate.pipe';
import { ExchangeImportCheckDialogService } from '../exchange-import-check-dialog/exchange-import-check-dialog.service';
import { AlertWindowService } from '../../../widgets/alert-window/alert-window.service';
import { StoreQuerierService } from '../../../services/store-querier.service';
import { SystemUser } from '../../../tamalelibs/models/user.model';

@Component({
    selector: 'tam-exchange-import-list-dialog',
    templateUrl: './exchange-import-list-dialog.component.html',
    styleUrls: ['./exchange-import-list-dialog.component.scss']
})
export class ExchangeImportListDialogComponent implements OnInit, OnDestroy {
    //#region public properties
    currentUser: SystemUser;
    dialogWidth = 710;
    importData: ExchangeImportDataModel;
    isCreateNewContact = false;
    isOpened: boolean;
    titleName = 'Import Contacts';
    //#endregion

    //#region private properties

    /**
    * the subscription array that would be unsubscribed on destroying.
    */
    private _destroySubscriptions: Array<Subscription> = [];

    //#endregion

    constructor(
        private _alertWindow: AlertWindowService,
        private _dialogService: DialogService,
        private _exchangeImportCheckDiglogService: ExchangeImportCheckDialogService,
        private _exchangeImportListDialogService: ExchangeImportListDialogService,
        private _exchangeService: ExchangeService,
        private _storeQuerier: StoreQuerierService,
        private _toast: ToastService) { }

    //#region public methods

    ngOnInit() {
        this.currentUser = this._storeQuerier.getCurrentUser();
        this._destroySubscriptions.push(
            this._exchangeImportListDialogService.dialogOpen$.pipe(
                filter(action => !this.isOpened),
            ).subscribe((dialogOpenOptions: ExchangeListDialogOptions) => {
                this.importData = dialogOpenOptions.data;
                this.importData.contacts.forEach(element => {
                    for (let i = 0; i < element.duplicates.length; i++) {
                        const data = element.duplicates[i];
                        if (!data.ownerID) {
                            data['isSelected'] = true;
                            element['tamaleId'] = data.id;
                            break;
                        }
                    }
                    if (element.duplicates.length > 0 && element.duplicates.findIndex(item => !item.ownerID) === -1) {
                        element.select = false;
                    }
                });
                this.isOpened = true;
            }),
            this._exchangeImportListDialogService.dialogClose$.subscribe(() => {
                this.isOpened = false;
            }));
    }

    ngOnDestroy(): void {
        this._destroySubscriptions.forEach(subscription => subscription.unsubscribe());
        this._destroySubscriptions = [];
    }

    onCancelBtnClick(event?) {
        this.isOpened = false;
    }

    onCloseBtnClick(event?) {
        const subscription = this._alertWindow.warn(StringLiteralsPipe.translate('general.warning'),
            [StringLiteralsPipe.translate('general.are_you_sure_you_want_to_close_the_dialog')], StringLiteralsPipe.translate('general.close')).subscribe((result: boolean) => {
                if (result) {
                    this.isOpened = false;
                    this._exchangeImportCheckDiglogService.dialogClose$.next();
                }
                subscription.unsubscribe();
            });
    }

    /**
     * If user click new contact label ,need remove tamaleId.
     *
     * @param {*} item
     * @memberof ExchangeImportListDialogComponent
     */
    onClickCreateNewContact(item) {
        this.importData.contacts.forEach(element => {
            if (item === element) {
                element['isCreateNewContact'] = true;
                element.select = true;
                element['tamaleIdCopy'] = element['tamaleId'];
                delete element['tamaleId'];
            }
        });
    }

    /**
     *
     * Click Undo will revert status of isCreateNewContact.
     * @param {*} item
     * @memberof ExchangeImportListDialogComponent
     */
    onClickUndo(item) {
        this.importData.contacts.forEach(element => {
            if (item === element) {
                element['isCreateNewContact'] = false;
                element['tamaleId'] = element['tamaleIdCopy'];
            }
        });
    }

    /**
     * Logic for selected duplicate item.
     *
     * @param {*} duplicateItem
     * @param {*} item
     * @memberof ExchangeImportListDialogComponent
     */
    onClickDuplicateContactItem(duplicateItem, item) {
        this.importData.contacts.forEach(element => {
            if (item === element) {
                element.duplicates.forEach(data => {
                    let isEnableDuplicateContactStatus = false;
                    // 1. the duplciate item was not connected by other user
                    if (!duplicateItem.ownerID) {
                        isEnableDuplicateContactStatus = true;
                    } else {
                        // 2. the duplicate item was connnted by other user (not current user)
                        if (duplicateItem.ownerID !== this.currentUser.id) {
                            isEnableDuplicateContactStatus = true;
                            element.syncType = 1;
                        }
                    }
                    // need to set duplicate item status in above two cases.
                    if (isEnableDuplicateContactStatus) {
                        if (duplicateItem === data) {
                            data['isSelected'] = !data['isSelected'];
                            if (data['isSelected']) {
                                element.select = true;
                                element['tamaleId'] = data.id;
                            } else {
                                if (element['tamaleId']) {
                                    delete element['tamaleId'];
                                    element.select = false;
                                }
                            }
                        } else {
                            data['isSelected'] = false;
                        }
                    }
                });
            }
        });
    }

    /**
     * Click import button and send import request.
     *
     * @memberof ExchangeImportListDialogComponent
     */
    onClickImport() {
        // get the selected duplicate data
        const contacts = new Array<MergedContactBase>();
        const needCreateNewContact = [];

        this.importData.contacts.forEach(data => {
            // new contact or no confliction item need to send to server
            if (data['isCreateNewContact'] ||
                data.duplicates === undefined ||
                (data.duplicates && data.duplicates.length === 0)) {
                needCreateNewContact.push({
                    exchange: data.exchange,
                    select: data.select
                });
            } else {
                // confliction items
                data.duplicates.forEach(item => {
                    if (item['isSelected']) {
                        const contact = new MergedContactBase();
                        contact.exchange = new ExchangeModel();
                        contact.exchange.id = data.exchange.id;
                        contact.exchange.name = data.exchange.name;
                        contact.exchange.emailAddress = data.exchange.emailAddress;
                        contact.tamaleId = item.id;
                        contact.select = true;
                        contact.syncType = data.syncType;
                        contacts.push(contact);
                    }
                });
            }
        });
        const exchangeUserId = this.importData.exchangeUserId;
        const tamaleUserId = this.importData.tamaleUserId;
        const duplicateData = new ExchangeImportInputDataModel(exchangeUserId, tamaleUserId, contacts);

        // get the duplicate data by selected item
        this._exchangeService.exchangeCompareDataWithTamale(duplicateData)
            .pipe(
                catchError((error) => {
                    this._toast.notify({
                        style: NotificationStyles.Error,
                        message: error.message
                    });
                    return observableOf(null);
                }),
                take(1),
                filter(res => !!res)
            )
            .subscribe(data => {
                const compareData = ExchangeImportDetailDataModel.parse(data);
                const compareResult = this._exchangeService.getDuplicateData(compareData);
                let isExistDuplicateItems = false;
                const duplicateItemList = compareResult.map(item => item.duplicates);
                duplicateItemList.forEach(item => {
                    if (item.length > 0) {
                        isExistDuplicateItems = true;
                    }
                });
                if (isExistDuplicateItems) {
                    const playload = {
                        originalData: compareData,
                        duplicateData: compareResult,
                        needCreateNewContact: needCreateNewContact
                    };
                    const dialogAction = new DialogAction(DialogType.ExchangeDuplicateDialog, playload);
                    this._dialogService.dialogOpen$.next(dialogAction);
                } else {
                    // create server parameter
                    const returnContacts = new Array<NeedReturnContact>();
                    compareResult.forEach(_data => {
                        const contactDetail = new ExchangeContactDetail();
                        this._exchangeService.getContactDetailByNoConflictateItems(_data.sames, contactDetail);
                        const exchange = _data.exchange;
                        const tamaleId = _data.tamaleId;
                        const syncType = _data.syncType;
                        const returnContact = new NeedReturnContact(contactDetail, exchange, tamaleId, true, syncType);
                        returnContacts.push(returnContact);
                    });
                    // need to add create contact to the retrun container
                    needCreateNewContact.forEach(contact => {
                        const item = new NeedReturnContact(null, contact.exchange, null, contact.select);
                        returnContacts.push(item);
                    });
                    const _exchangeUserId = this.importData.exchangeUserId;
                    const _tamaleUserId = this.importData.tamaleUserId;
                    const returnData = new ExchangeReturnDataModel(returnContacts, _exchangeUserId, _tamaleUserId);
                    // server change, need to create return data to server
                    this._destroySubscriptions.push(
                        this._exchangeService.postExchangeImportContactList(returnData)
                            .pipe(
                                catchError((error) => {
                                    this._toast.notify({
                                        style: NotificationStyles.Error,
                                        message: error.message
                                    });
                                    return observableOf(null);
                                }),
                                take(1),
                                filter(res => !!res)
                            )
                            .subscribe(() => {
                                this._toast.notify({
                                    style: NotificationStyles.Info,
                                    message: StringLiteralsPipe.translate('general.importing_in_progress')
                                });
                                this._closeDialog();
                            }));
                }
            });
    }

    /**
     * Get job title by duplicate item
     * @param duplicateItem
     * @returns jobTitle
     */
    getJobTitleByDuplicateItem(duplicateItem): string {
        let jobTitle = '';

        if (duplicateItem &&
            duplicateItem.webJobTitle &&
            duplicateItem.webJobTitle.length > 0) {
            jobTitle = duplicateItem.webJobTitle.map(item => item.name).join(', ');
        }
        return jobTitle;
    }

    //#endregion

    //#region private methods

    private _closeDialog(): void {
        this._exchangeImportListDialogService.dialogClose$.next();
        this._exchangeImportCheckDiglogService.dialogClose$.next();
    }

    //#endregion
}
